'use client';

import { faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { faCalendar, faMapMarkerAlt, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getCDNURL } from '@pickleballinc/lib/get-cdn-url';
import DatePicker from '@pickleballinc/react-ui/DatePicker';
import { Button } from '@pickleballinc/ui/components/ui/button';
import { Drawer, DrawerContent, DrawerPortal } from '@pickleballinc/ui/components/ui/drawer';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@pickleballinc/ui/components/ui/select';
import dayjs from 'dayjs';
import Link from 'next/link';
import { useState } from 'react';
import { createPortal } from 'react-dom';

import { NextEventGameday } from '@/entities/NextEventGameday';
import { TeamLeagueLocation } from '@/entities/TeamLeagueLocation';
import { useAllEventsQuery } from '@/hooks/banner/useAllEventsQuery';
import { useTeamLeagueLocationsQuery } from '@/hooks/system-locations/useTeamLeagueLocationsQuery';
import { useTeamsOptionsQuery } from '@/hooks/teams/useTeamsOptionsQuery';
import { DateUtil } from '@/utils/date';

interface Props {
	isOpen: boolean;
	onClose: () => void;
	teamLeagueId: string;
	teamLeagueUuid: string;
	seasonUuid: string;
}

export const ScheduledEventsModal = ({ isOpen, onClose, teamLeagueId, teamLeagueUuid, seasonUuid }: Props) => {
	const [startDate, setStartDate] = useState<string | undefined>();
	const [locationUuid, setLocationUuid] = useState<string | undefined>();
	const [teamUuid, setTeamUuid] = useState<string | undefined>();

	const { data: eventsData, isLoading: isLoadingEvents } = useAllEventsQuery({ teamLeagueUuid, seasonUuid, locationUuid, teamUuid, startDate });
	const { data: locationsData, isLoading: isLoadingLocations } = useTeamLeagueLocationsQuery(teamLeagueUuid);
	const { data: teamsData, isLoading: isLoadingTeams } = useTeamsOptionsQuery({ seasonUuid });

	const events = eventsData?.data ?? [];
	const locations = locationsData?.data ?? [];
	const teams = teamsData?.data ?? [];

	const handleLocationChange = (value: string) => setLocationUuid(value === 'all' ? undefined : value);
	const handleTeamChange = (value: string) => setTeamUuid(value === 'all' ? undefined : value);
	const handleDateChange = (date: Date) => setStartDate(dayjs(date).format('YYYY-MM-DD'));

	const sharedContent = (
		<ScheduledEventsContent
			events={events}
			locations={locations}
			teams={teams}
			teamLeagueId={teamLeagueId}
			startDate={startDate}
			locationUuid={locationUuid}
			teamUuid={teamUuid}
			isLoadingEvents={isLoadingEvents}
			isLoadingLocations={isLoadingLocations}
			isLoadingTeams={isLoadingTeams}
			onClose={onClose}
			onLocationChange={handleLocationChange}
			onTeamChange={handleTeamChange}
			onDateChange={handleDateChange}
			onClearDate={() => setStartDate(undefined)}
		/>
	);

	const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 768;

	if (isDesktop) {
		if (!isOpen) {
			return null;
		}

		// Non-blocking overlay (so the portaled DatePicker/Select filters stay clickable) rendered
		// via a portal to guarantee viewport-fixed positioning and top-level stacking.
		return createPortal(
			<div className="fixed inset-0 z-50 flex items-center justify-center p-4">
				<button type="button" aria-label="Close" className="absolute inset-0 bg-black/80" onClick={onClose} />
				<div className="relative w-full max-w-[572px] max-h-[80vh] overflow-hidden rounded-lg bg-white shadow-lg">{sharedContent}</div>
			</div>,
			document.body
		);
	}

	return (
		<>
			{isOpen ? <div className="fixed inset-0 z-40 bg-black/80" onClick={onClose} /> : null}
			<Drawer modal={false} open={isOpen} onClose={onClose}>
				<DrawerPortal>
					<DrawerContent className="flex w-full flex-col bg-white pb-4 pt-1 max-h-[90vh] overflow-hidden">{sharedContent}</DrawerContent>
				</DrawerPortal>
			</Drawer>
		</>
	);
};

interface ContentProps {
	events: NextEventGameday[];
	locations: TeamLeagueLocation[];
	teams: { value: string; label: string }[];
	teamLeagueId: string;
	startDate?: string;
	locationUuid?: string;
	teamUuid?: string;
	isLoadingEvents: boolean;
	isLoadingLocations: boolean;
	isLoadingTeams: boolean;
	onClose: () => void;
	onLocationChange: (value: string) => void;
	onTeamChange: (value: string) => void;
	onDateChange: (date: Date) => void;
	onClearDate: () => void;
}

const ScheduledEventsContent = ({
	events,
	locations,
	teams,
	teamLeagueId,
	startDate,
	locationUuid,
	teamUuid,
	isLoadingEvents,
	isLoadingLocations,
	isLoadingTeams,
	onClose,
	onLocationChange,
	onTeamChange,
	onDateChange,
	onClearDate
}: ContentProps) => {
	return (
		<div className="flex flex-col gap-4 p-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<h2 className="text-2xl font-medium text-black">All events</h2>
				<button onClick={onClose} className="flex items-center justify-center rounded-sm opacity-70 hover:opacity-100 transition-opacity">
					<FontAwesomeIcon icon={faXmark} className="size-5 text-black" />
					<span className="sr-only">Close</span>
				</button>
			</div>

			{/* Divider */}
			<div className="h-px bg-gray-200" />

			{/* Subtitle */}
			<p className="text-sm font-medium text-gray-600">You can view the list of all the events for the next events</p>

			{/* Filters */}
			<div className="flex flex-col gap-3">
				<div className="flex gap-4">
					{/* Date filter */}
					<div className="flex flex-col gap-1.5 flex-1">
						<p className="text-sm font-medium text-gray-700">Date</p>
						<DatePicker
							onChange={onDateChange}
							selected={startDate ? new Date(startDate) : null}
							closeOnSelection
							wrapperZIndex="100"
							menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
							datePickerTheme="orange"
							showMonthYearPicker={false}
						>
							<div className="flex h-9 w-full cursor-pointer items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-xs">
								<span className={startDate ? 'text-gray-900' : 'text-gray-500'}>
									{startDate ? dayjs(startDate).format('MMM D, YYYY') : 'Filter by date'}
								</span>
								{startDate ? (
									<button
										onClick={(e) => {
											e.stopPropagation();
											onClearDate();
										}}
										className="ml-2 text-gray-400 hover:text-gray-600"
									>
										<FontAwesomeIcon icon={faXmark} className="size-3" />
									</button>
								) : (
									<svg className="size-4 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
										<path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
									</svg>
								)}
							</div>
						</DatePicker>
					</div>

					{/* Location filter */}
					<div className="flex flex-col gap-1.5 flex-1">
						<p className="text-sm font-medium text-gray-700">Location</p>
						<Select value={locationUuid ?? 'all'} onValueChange={onLocationChange}>
							<SelectTrigger
								className="bg-white h-9 focus:ring-0"
								icon={({ className }: { className: string }) => <FontAwesomeIcon icon={faChevronDown} className={className} />}
							>
								<SelectValue placeholder="Filter by location" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">Filter by location</SelectItem>
								{isLoadingLocations ? (
									<SelectItem value="loading-locations" disabled>
										Loading...
									</SelectItem>
								) : (
									locations.map((location) => (
										<SelectItem key={location.uuid} value={location.uuid}>
											{location.venue}
										</SelectItem>
									))
								)}
							</SelectContent>
						</Select>
					</div>
				</div>

				{/* Team filter */}
				<div className="flex flex-col gap-1.5">
					<p className="text-sm font-medium text-gray-700">Team name</p>
					<Select value={teamUuid ?? 'all'} onValueChange={onTeamChange}>
						<SelectTrigger
							className="bg-white h-9 focus:ring-0"
							icon={({ className }: { className: string }) => <FontAwesomeIcon icon={faChevronDown} className={className} />}
						>
							<SelectValue placeholder="Filter by name" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">Filter by name</SelectItem>
							{isLoadingTeams ? (
								<SelectItem value="loading-teams" disabled>
									Loading...
								</SelectItem>
							) : (
								teams.map((team) => (
									<SelectItem key={team.value} value={team.value}>
										{team.label}
									</SelectItem>
								))
							)}
						</SelectContent>
					</Select>
				</div>
			</div>

			{/* Event list */}
			<div className="flex flex-col gap-3 h-[300px] overflow-y-auto">
				{isLoadingEvents ? (
					<>
						<EventCardSkeleton />
						<EventCardSkeleton />
						<EventCardSkeleton />
					</>
				) : (
					<>
						{events.map((event) => (
							<EventCard key={event.uuid} event={event} teamLeagueId={teamLeagueId} />
						))}
						{events.length === 0 && <p className="text-sm text-gray-500 text-center py-4">No events found</p>}
					</>
				)}
			</div>

			{/* Footer */}
			<div className="flex gap-4">
				<Button variant="outline" className="flex-1 font-semibold" onClick={onClose}>
					Close
				</Button>
				<Button asChild className="flex-1 bg-orange-500 hover:bg-orange-600 font-semibold text-white">
					<Link href={`/team-leagues/${teamLeagueId}/gameday`} onClick={onClose}>
						Go to schedule
					</Link>
				</Button>
			</div>
		</div>
	);
};

const EventCard = ({ event, teamLeagueId }: { event: NextEventGameday; teamLeagueId: string }) => {
	const abbreviation = event.timezone?.abbreviation;
	const startDate = DateUtil.formatUTCInTimezone(event.startDate, 'MMM D, YYYY', event.timezone?.ianaTzIdentifier);

	const locationParts = [
		event.location?.venue,
		event.location?.streetAddress,
		event.location?.city,
		event.location?.stateInfo?.abbreviation
	].filter(Boolean);

	return (
		<div className="rounded-xl border border-gray-300 overflow-hidden shrink-0">
			<div className="flex items-center gap-2.5 p-3">
				{event.logo ? (
					<img
						src={getCDNURL(event.logo.url, 292, 160)}
						alt={event.logo?.uuid}
						className="w-36 h-20 object-contain rounded-lg border border-gray-200 shrink-0"
					/>
				) : null}
				<div className="flex flex-col gap-1 min-w-0">
					<p className="text-xs text-gray-600 truncate">{event.title}</p>
					<p className="text-sm font-medium text-gray-900 truncate">{event.title}</p>
					<div className="flex items-center gap-1 text-xs text-gray-600">
						<FontAwesomeIcon icon={faCalendar} className="size-3.5 shrink-0" />
						<span>
							{startDate} {abbreviation}
						</span>
					</div>
					{locationParts.length > 0 && (
						<div className="flex items-center gap-1 text-xs text-gray-600">
							<FontAwesomeIcon icon={faMapMarkerAlt} className="size-3.5 shrink-0" />
							<span className="truncate">{locationParts.join(', ')}</span>
						</div>
					)}
				</div>
			</div>
			<div className="bg-gray-50 px-3 py-2">
				<Link
					href={`/team-leagues/${teamLeagueId}/event/${event.uuid}`}
					className="block w-full text-center text-xs font-semibold text-orange-500 hover:text-orange-600 transition-colors"
				>
					Event schedule
				</Link>
			</div>
		</div>
	);
};

const EventCardSkeleton = () => (
	<div className="rounded-xl border border-gray-200 overflow-hidden animate-pulse shrink-0">
		<div className="flex items-center gap-2.5 p-3">
			<div className="w-36 h-20 rounded-lg bg-gray-200 shrink-0" />
			<div className="flex flex-col gap-2 min-w-0 flex-1">
				<div className="h-3 bg-gray-200 rounded w-3/4" />
				<div className="h-3.5 bg-gray-200 rounded w-1/2" />
				<div className="h-3 bg-gray-200 rounded w-2/3" />
				<div className="h-3 bg-gray-200 rounded w-1/2" />
			</div>
		</div>
		<div className="bg-gray-50 px-3 py-2">
			<div className="h-3 bg-gray-200 rounded w-1/4 mx-auto" />
		</div>
	</div>
);
